import {useState, useEffect, useMemo, useCallback, useRef} from "react";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useLocation} from "wouter";
import {queryClient} from "@/lib/queryClient";
import { setActiveChatContext } from "@/state/activeChatContext";
import {type Topic, type ChatThread} from "@shared/schema";
import {topicsService} from "@/services/topicsService";
import {chatThreadsService} from "@/services/chatThreadsService";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {type MastermindSpace} from "@shared/schema";
import {useViewPreferences, type SortDirection} from "@/hooks/use-view-preferences";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {PinVerificationDialog} from "@/components/PinVerificationDialog";
import {Button} from "@/components/ui/button";
import {Card} from "@/components/ui/card";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Badge} from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {Label} from "@/components/ui/label";
import {useToast} from "@/hooks/use-toast";
import {
    Plus,
    Pencil,
    Trash2,
    ChevronDown,
    ChevronRight,
    MessageSquare,
    MessageCircle,
    Link2,
    Folder,
    FolderOpen,
    // FolderOutput,
    Search,
    ArrowUp,
    ArrowDown,
    LayoutGrid,
    List,
    Lock,
    MoreVertical,
    LockOpen,
    FileText,
    X,
    Share2,
    Brain
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {Switch} from "@/components/ui/switch";
import {ThreadLinkingDialog} from "@/components/ThreadLinkingDialog";
import {ShareTopicDialog} from "@/components/ShareTopicDialog";
import {EntityLockButton} from "@/components/EntityLockButton";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {IconSearch, IconSelectMenu} from "@/components/CompactToolbar";
import {topicIconOptions, getTopicIcon} from "@/lib/utils";
import {DocumentLinkerDrawer} from "@/components/DocumentLinkerDrawer";
import {knowledgeBaseService, type KnowledgeBaseItem} from "@/services/knowledgeBaseService";
import {useLibrary} from "@/contexts/LibraryContext";
import {ragService} from "@/services/ragService";
import {DocumentItem} from "@/components/DocumentItem";
import {DocumentViewerModal} from "@/components/DocumentViewerModal";
import {ThreadItem} from "@/components/ThreadItem";
import {FileUploadToNote} from "@/components/FileUploadToNote";
import {GoogleDrivePicker} from "@/components/GoogleDrivePicker";
import {TopicDocumentModal} from "@/components/TopicDocumentModal";
import {SiGoogledrive} from "react-icons/si";
import {useAuth} from "@/contexts/AuthContext";
import {pinService} from "@/services/pinService";
import {
    effectiveItemActions,
    shouldRestrictEditorLinksToCurrentSharedSpace,
    canCreateTopicsInSpace,
    type ItemOverrideMap,
    type SpaceMemberRole,
} from "@/services/sharedSpacePermissionService";
import { partitionMerged, isTopicMerged } from "@/lib/spaceMergeUi";
import { SpaceMergedSection, MergedRowChrome } from "@/components/space-merge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";


type TopicSortField = "name" | "createdAt" | "updatedAt";

/** Truncate title to maxLength and append "..." for small screens / dropdowns */
function truncateTitle(title: string | undefined | null, maxLength: number): string {
    if (!title) return "";
    return title.length <= maxLength ? title : title.slice(0, maxLength) + "...";
}

const TITLE_MAX_LENGTH = 22;

interface TopicsManagementProps {
    spaceId: string;
    onTopicSelect?: (topicId: string | null) => void;
    selectedTopicId?: string | null;
    topics?: Topic[];
    allThreads?: (ChatThread | Omit<ChatThread, 'messages'> & { messageCount?: number })[];
    allSpaces?: MastermindSpace[];
    allDocuments?: KnowledgeBaseItem[];
    /** From ResizeObserver in MastermindSpaceDetail - center column width so list uses exact available space */
    contentWidthPx?: number | null;
    /** When set (e.g. from URL ?expandTopic=ID), expand this topic accordion on load (e.g. when returning from chat) */
    initialExpandedTopicId?: string | null;
    /** Current user's role in this space (for shared space document/thread permissions). */
    spaceMemberRole?: SpaceMemberRole | null;
    spaceOwnerUserId?: string | null;
    itemOverrides?: ItemOverrideMap;
    /** True when viewing a space shared via link (not owned by current user). */
    isSharedSpace?: boolean;
}

export function TopicsManagement({
                                     spaceId,
                                     onTopicSelect,
                                     selectedTopicId,
                                     topics,
                                     allThreads: allThreadsProp,
                                     allSpaces: allSpacesProp,
                                     allDocuments: allDocumentsProp,
                                     isSharedSpace = false,
                                     spaceDataBeingLoaded: isLoading,
                                     refreshKnowledge,
                                     contentWidthPx,
                                     initialExpandedTopicId,
                                     spaceMemberRole = null,
                                     spaceOwnerUserId = null,
                                     itemOverrides,
                                 }) {


    const {toast} = useToast();
    const {user} = useAuth();
    const { currentWorkspaceId } = useWorkspace();
    const {setSelectedDocumentId, setDocumentEditSpaceContextId} = useLibrary();

    const restrictEditorLinksToCurrentSpace = useMemo(
        () =>
            shouldRestrictEditorLinksToCurrentSharedSpace(
                isSharedSpace,
                spaceMemberRole,
                spaceOwnerUserId,
                user?.id
            ),
        [isSharedSpace, spaceMemberRole, spaceOwnerUserId, user?.id]
    );
    const canCreateTopics = canCreateTopicsInSpace(spaceMemberRole);
    const isSpaceOwnerUser =
        !!user?.id && !!spaceOwnerUserId && user.id === spaceOwnerUserId;

    const getTopicItemActions = useCallback(
        (topic: Topic) =>
            effectiveItemActions(
                spaceMemberRole,
                "topic",
                topic.id,
                topic.createdByUserId,
                user?.id,
                spaceOwnerUserId,
                itemOverrides
            ),
        [spaceMemberRole, user?.id, spaceOwnerUserId, itemOverrides]
    );
    const [, setLocation] = useLocation();
    const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
    const [documentToView, setDocumentToView] = useState<KnowledgeBaseItem | null>(null);
    const [showPinDialogForDoc, setShowPinDialogForDoc] = useState(false);
    const [pendingDocumentIdToOpen, setPendingDocumentIdToOpen] = useState<string | null>(null);

    const {data: docPinStatus} = useQuery({
        queryKey: ["pin-status"],
        queryFn: () => pinService.getStatus(),
    });

    const handleDocumentClickFromTopics = useCallback(
        (documentId: string, isLocked?: boolean) => {
            const doc = allDocumentsProp?.find((d: KnowledgeBaseItem) => d.id === documentId);
            if (isSharedSpace && doc && doc.userId !== user?.id) {
                const actions = effectiveItemActions(
                    spaceMemberRole,
                    "document",
                    documentId,
                    doc.userId,
                    user?.id,
                    spaceOwnerUserId,
                    itemOverrides,
                );
                if (!actions.canEdit) {
                    setDocumentToView(doc);
                    return;
                }
            }
            if (isLocked && !docPinStatus?.isSessionValid) {
                setPendingDocumentIdToOpen(documentId);
                setShowPinDialogForDoc(true);
                return;
            }
            if (spaceId) {
                setDocumentEditSpaceContextId(spaceId);
            }
            setSelectedDocumentId(documentId);
        },
        [allDocumentsProp, isSharedSpace, user?.id, docPinStatus?.isSessionValid, setSelectedDocumentId, setDocumentEditSpaceContextId, spaceId, spaceMemberRole, spaceOwnerUserId, itemOverrides]
    );

    const handleDocPinSuccessForTopics = useCallback(() => {
        if (pendingDocumentIdToOpen) {
            if (spaceId) {
                setDocumentEditSpaceContextId(spaceId);
            }
            setSelectedDocumentId(pendingDocumentIdToOpen);
            setPendingDocumentIdToOpen(null);
            setShowPinDialogForDoc(false);
        }
    }, [pendingDocumentIdToOpen, setSelectedDocumentId, setDocumentEditSpaceContextId, spaceId]);

    // When returning from chat with ?expandTopic=ID, expand that topic accordion (after topics may have loaded)
    useEffect(() => {
        if (initialExpandedTopicId) {
            const id = String(initialExpandedTopicId);
            setExpandedTopics(prev => new Set([...prev, id]));
        }
    }, [initialExpandedTopicId, topics]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
    const [deletingTopic, setDeletingTopic] = useState<Topic | null>(null);
    const [parentTopicForCreate, setParentTopicForCreate] = useState<Topic | null>(null);

    const [newTopicName, setNewTopicName] = useState("");
    const [newTopicDescription, setNewTopicDescription] = useState("");
    const [newTopicIcon, setNewTopicIcon] = useState("folder");
    const [newTopicTags, setNewTopicTags] = useState("");

    const [searchQuery, setSearchQuery] = useState("");

    // Track unlocked topics for this session and PIN dialog state
    const [unlockedTopicIds, setUnlockedTopicIds] = useState<Set<string>>(new Set());
    const [showPinDialogForTopic, setShowPinDialogForTopic] = useState<string | null>(null);
    const [pendingTopicIdForChat, setPendingTopicIdForChat] = useState<string | null>(null);
    const [pendingExpandTopicId, setPendingExpandTopicId] = useState<string | null>(null);

    // Thread operations state
    const [threadToRename, setThreadToRename] = useState<ChatThread | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [threadToLink, setThreadToLink] = useState<ChatThread | null>(null);
    const [threadToLock, setThreadToLock] = useState<ChatThread | null>(null);
    const [threadToDelete, setThreadToDelete] = useState<ChatThread | null>(null);
    const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);

    // Topic linking state
    const [topicToLink, setTopicToLink] = useState<Topic | null>(null);
    const [topicToShare, setTopicToShare] = useState<Topic | null>(null);

    // Move topic state
    const [topicToMove, setTopicToMove] = useState<Topic | null>(null);
    const [selectedTargetSpaceId, setSelectedTargetSpaceId] = useState<string>("");

    const {
        viewMode,
        setViewMode,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
    } = useViewPreferences<TopicSortField>({
        storageKey: `topics.${spaceId}`,
        defaultViewMode: "list",
        defaultSortField: "name",
        defaultSortDirection: "asc",
    });

    // Use topics from prop if provided, otherwise fetch (for backward compatibility)
    /*const { data: topicsFromQuery = [], isLoading } = useQuery<Topic[]>({
      queryKey: ["/api/spaces", spaceId, "topics"],
      queryFn: async () => {
        if (!spaceId) return [];
        return await topicsService.getTopics(spaceId);
      },
      enabled: !!spaceId && topicsProp === undefined,
    });*/

    // const topics = topicsProp ?? topicsFromQuery;

    const filterAndSortTopicList = useCallback(
        (list: Topic[]) =>
            list
                .filter((topic) => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                        topic.name.toLowerCase().includes(query) ||
                        topic.description?.toLowerCase().includes(query) ||
                        topic.tags?.some((tag) => tag.toLowerCase().includes(query))
                    );
                })
                .sort((a, b) => {
                    let comparison = 0;
                    switch (sortField) {
                        case "name":
                            comparison = a.name.localeCompare(b.name);
                            break;
                        case "createdAt":
                            comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                            break;
                        case "updatedAt":
                            comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
                            break;
                    }
                    return sortDirection === "asc" ? comparison : -comparison;
                }),
        [searchQuery, sortField, sortDirection]
    );

    const { native: topicsNative, merged: topicsMerged } = useMemo(() => {
        const rootList = (topics ?? []).filter((t) => !t.parentTopicId);
        return partitionMerged<Topic>(rootList, (t) => isTopicMerged(t));
    }, [topics]);

    const filteredSortedNativeTopics = useMemo(
        () => filterAndSortTopicList(topicsNative),
        [topicsNative, filterAndSortTopicList]
    );

    const filteredSortedMergedTopics = useMemo(
        () => filterAndSortTopicList(topicsMerged),
        [topicsMerged, filterAndSortTopicList]
    );

    const childTopicsByParentId = useMemo(() => {
        const map = new Map<string, Topic[]>();
        for (const t of topics ?? []) {
            if (t.parentTopicId) {
                const arr = map.get(t.parentTopicId) ?? [];
                arr.push(t);
                map.set(t.parentTopicId, arr);
            }
        }
        return map;
    }, [topics]);

    const getChildTopics = useCallback(
        (parentId: string) => filterAndSortTopicList(childTopicsByParentId.get(parentId) ?? []),
        [childTopicsByParentId, filterAndSortTopicList]
    );

    const hasAnyFilteredTopic =
        filteredSortedNativeTopics.length > 0 || filteredSortedMergedTopics.length > 0;

    const createTopicMutation = useMutation({
        mutationFn: async (topicData: { name: string; description?: string; icon?: string; tags?: string[]; parentTopicId?: string }) => {
            if (!spaceId) throw new Error("Space ID is required");
            return await topicsService.createTopic({
                spaceId,
                name: topicData.name,
                description: topicData.description,
                icon: topicData.icon,
                tags: topicData.tags,
                parentTopicId: topicData.parentTopicId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]}); // Update sidebar
            toast({
                title: "Topic Created",
                description: "Your new topic has been created successfully",
            });
            handleCloseCreateDialog();
        },
        onError: (error: any) => {
            toast({
                title: "Failed to Create Topic",
                description: error.message || "Something went wrong",
                variant: "destructive",
            });
        },
    });

    const updateTopicMutation = useMutation({
        mutationFn: async ({id, updates}: {
            id: string;
            updates: Partial<Topic> & { icon?: string; tags?: string[] }
        }) => {
            return await topicsService.updateTopic(id, updates, spaceId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]}); // Update sidebar
            toast({
                title: "Topic Updated",
                description: "Your topic has been updated successfully",
            });
            handleCloseEditDialog();
        },
        onError: (error: any) => {
            toast({
                title: "Failed to Update Topic",
                description: error.message || "Something went wrong",
                variant: "destructive",
            });
        },
    });

    const deleteTopicMutation = useMutation({
        mutationFn: async (topicId: string) => {
            return await topicsService.deleteTopic(topicId, spaceId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]}); // Update sidebar
            toast({
                title: "Topic Deleted",
                description: "The topic and its subtopics have been deleted",
            });
            setIsDeleteDialogOpen(false);
            setDeletingTopic(null);
            if (selectedTopicId === deletingTopic?.id) {
                onTopicSelect?.(null);
            }
        },
        onError: (error: any) => {
            toast({
                title: "Failed to Delete Topic",
                description: error.message || "Something went wrong",
                variant: "destructive",
            });
        },
    });

    // Thread mutations
    const renameThreadMutation = useMutation({
        mutationFn: async ({threadId, title}: { threadId: string; title: string }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {title}, currentWorkspaceId ?? null);
            if (!result) throw new Error("Failed to rename thread");
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({
                title: "Thread renamed",
                description: "The thread title has been updated.",
            });
            setThreadToRename(null);
            setRenameValue("");
        },
        onError: (error: Error) => {
            toast({
                title: "Rename failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateThreadSpacesMutation = useMutation({
        mutationFn: async ({threadId, spaceIds}: { threadId: string; spaceIds: string[] }) => {
            const result = await chatThreadsService.updateChatThread(
                threadId,
                {linkedSpaceIds: spaceIds},
                currentWorkspaceId ?? null,
                spaceId
            );
            if (!result) throw new Error("Failed to update spaces");
            return result;
        },
        onSuccess: async (updatedThread) => {
            // Optimistically update the cache immediately to prevent UI flicker
            queryClient.setQueriesData<ChatThread[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(thread =>
                        thread.id === updatedThread.id
                            ? {...thread, linkedSpaceIds: updatedThread.linkedSpaceIds || []}
                            : thread
                    );
                }
            );

            // Refetch all thread-related queries to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);

            // Invalidate related queries for other components
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});

            toast({
                title: "Spaces updated",
                description: "Thread spaces have been updated",
            });
        },
        onError: (error: Error) => {
            // Refetch on error to restore correct state
            queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({
                title: "Update failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateThreadTopicMutation = useMutation({
        mutationFn: async ({threadId, topicId}: { threadId: string; topicId: string | null }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {topicId}, currentWorkspaceId ?? null);
            if (!result) throw new Error("Failed to update topic");
            return result;
        },
        onSuccess: async (updatedThread, {topicId}) => {
            // Optimistically update the cache immediately to prevent UI flicker
            queryClient.setQueriesData<ChatThread[]>(
                {queryKey: ["/api/chat-thread-summaries"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(thread =>
                        thread.id === updatedThread.id
                            ? {...thread, topicId: topicId || null}
                            : thread
                    );
                }
            );

            // Refetch all thread-related queries to ensure consistency
            await Promise.all([
                queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false}),
                queryClient.refetchQueries({queryKey: ["/api/chat-threads"], exact: false}),
            ]);

            // Invalidate related queries for other components
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            if (topicId) {
                queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "threads"]});
            }

            toast({
                title: "Thread linked",
                description: topicId ? "Thread has been linked to the topic" : "Thread has been unlinked from topic",
            });
        },
        onError: (error: Error) => {
            // Refetch on error to restore correct state
            queryClient.refetchQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
            toast({
                title: "Link failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateThreadTagsMutation = useMutation({
        mutationFn: async ({threadId, tags}: { threadId: string; tags: string[] }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {tags}, currentWorkspaceId ?? null);
            if (!result) throw new Error("Failed to update tags");
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            queryClient.invalidateQueries({queryKey: ["/api/chat-thread-summaries"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
        },
    });

    const toggleThreadLockMutation = useMutation({
        mutationFn: async ({threadId, lock}: { threadId: string; lock: boolean }) => {
            const result = await chatThreadsService.lockChatThread(threadId, lock, currentWorkspaceId ?? null);
            if (!result) throw new Error("Failed to toggle lock");
            return result;
        },
        onSuccess: (_, {lock}) => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({
                title: lock ? "Thread Locked" : "Thread Unlocked",
                description: lock ? "This thread is now protected with your PIN" : "Thread is now accessible",
            });
            setThreadToLock(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const deleteThreadMutation = useMutation({
        mutationFn: async (threadId: string) => {
            const result = await chatThreadsService.deleteChatThread(threadId);
            if (!result) throw new Error("Failed to delete thread");
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            queryClient.invalidateQueries({queryKey: ["/api/chat-thread-summaries"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({
                title: "Thread Deleted",
                description: "The thread has been permanently deleted",
            });
            setThreadToDelete(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Delete Failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // PIN status query
    const {data: pinStatus} = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
        queryKey: ["/api/pin/status"],
        queryFn: async () => {
            const { pinService } = await import("@/services/pinService");
            return pinService.getStatus();
        },
    });

    // Use allSpaces from prop if provided, otherwise fetch (workspace-scoped for move target)
    const {data: allSpacesFromQuery = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/mastermind-spaces", currentWorkspaceId ?? null],
        queryFn: async () => {
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
        enabled: !!topicToMove && allSpacesProp === undefined,
    });

    const allSpaces = allSpacesProp ?? allSpacesFromQuery;

    // Move topic mutation
    const moveTopicMutation = useMutation({
        mutationFn: async ({topicId, targetSpaceId}: { topicId: string; targetSpaceId: string }) => {
            return await topicsService.moveTopicToSpace(topicId, targetSpaceId);
        },
        onSuccess: (_, {topicId, targetSpaceId}) => {
            const targetSpace = allSpaces.find(s => s.id === targetSpaceId);
            queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "linked-spaces"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", targetSpaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            toast({
                title: "Topic Moved",
                description: `Topic moved to "${targetSpace?.name || 'new space'}"`,
            });
            setTopicToMove(null);
            setSelectedTargetSpaceId("");
        },
        onError: (error: Error) => {
            toast({
                title: "Move Failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const topicLinkRequiredSpaceIds = useMemo(
        () => (topicToLink ? [topicToLink.spaceId] : []),
        [topicToLink?.id, topicToLink?.spaceId]
    );

    const {data: topicLinkedSpaceIds} = useQuery({
        queryKey: ["/api/topics", topicToLink?.id, "linked-spaces"],
        queryFn: () => topicsService.getTopicLinkedSpaceIds(topicToLink!.id),
        enabled: !!topicToLink?.id,
    });

    const syncTopicLinkedSpacesMutation = useMutation({
        mutationFn: ({topicId, spaceIds}: {topicId: string; spaceIds: string[]}) =>
            topicsService.syncTopicLinkedSpaces(topicId, spaceIds),
        onSuccess: async (_, {topicId, spaceIds}) => {
            await queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "linked-spaces"]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            for (const sid of spaceIds) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", sid, "topics"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/all-topics"]});
            toast({
                title: "Spaces updated",
                description: "Topic spaces have been updated.",
            });
        },
        onError: async (error: Error, {topicId}) => {
            await queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "linked-spaces"]});
            toast({
                title: "Update failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleThreadLockClick = (thread: ChatThread) => {
        if (!pinStatus?.hasPinSet) {
            toast({
                title: "PIN Required",
                description: "Please set a security PIN in Settings first",
            });
            setLocation("/settings");
            return;
        }
        if (!pinStatus?.isSessionValid) {
            setThreadToLock(thread);
            setIsPinDialogOpen(true);
            return;
        }
        const shouldLock = !thread.isLocked;
        toggleThreadLockMutation.mutate({threadId: thread.id, lock: shouldLock});
    };

    const toggleExpanded = (topicId: string | number) => {
        const id = String(topicId);
        setExpandedTopics(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleCloseCreateDialog = () => {
        setIsCreateDialogOpen(false);
        setNewTopicName("");
        setNewTopicDescription("");
        setNewTopicIcon("folder");
        setNewTopicTags("");
        setParentTopicForCreate(null);
    };

    const handleOpenSubtopicDialog = (topic: Topic) => {
        setParentTopicForCreate(topic);
        setIsCreateDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setEditingTopic(null);
        setNewTopicName("");
        setNewTopicDescription("");
        setNewTopicIcon("folder");
        setNewTopicTags("");
    };

    const handleOpenEditDialog = (topic: Topic) => {
        setEditingTopic(topic);
        setNewTopicName(topic.name);
        setNewTopicDescription(topic.description || "");
        setNewTopicIcon(topic.icon || "folder");
        setNewTopicTags(topic.tags?.join(", ") || "");
        setIsEditDialogOpen(true);
    };

    const handleCreateTopic = () => {
        if (!newTopicName.trim()) {
            toast({
                title: "Name Required",
                description: "Please enter a topic name",
                variant: "destructive",
            });
            return;
        }

        if (!canCreateTopics) {
            toast({
                title: "View-only access",
                description: isSpaceOwnerUser
                    ? "You cannot create topics with your current access level in this space."
                    : "You are not the owner of this space and have viewer access. You cannot create topics here.",
                variant: "destructive",
            });
            return;
        }

        const tagsArray = newTopicTags
            .split(",")
            .map(t => t.trim())
            .filter(t => t.length > 0);

        createTopicMutation.mutate({
            name: newTopicName.trim(),
            description: newTopicDescription.trim() || undefined,
            icon: newTopicIcon.trim() || undefined,
            tags: tagsArray.length > 0 ? tagsArray : undefined,
            parentTopicId: parentTopicForCreate?.id,
        });
    };

    const handleUpdateTopic = () => {
        if (!editingTopic || !newTopicName.trim()) {
            toast({
                title: "Name Required",
                description: "Please enter a topic name",
                variant: "destructive",
            });
            return;
        }

        const tagsArray = newTopicTags
            .split(",")
            .map(t => t.trim())
            .filter(t => t.length > 0);

        updateTopicMutation.mutate({
            id: editingTopic.id,
            updates: {
                name: newTopicName.trim(),
                description: newTopicDescription.trim() || null,
                icon: newTopicIcon.trim() || null,
                tags: tagsArray,
            },
        });
    };

    const handleDeleteTopic = (topic: Topic) => {
        setDeletingTopic(topic);
        setIsDeleteDialogOpen(true);
    };

    const confirmDeleteTopic = () => {
        if (!deletingTopic || deleteTopicMutation.isPending) return;
        deleteTopicMutation.mutate(deletingTopic.id);
    };

    const isTopicLocked = (topic: Topic) =>
        topic?.isLocked === true || topic?.isLocked === "true";

    // Refs so PIN success handler always sees latest values (avoids stale closure after relogin)
    const showPinDialogForTopicRef = useRef<string | null>(null);
    const pendingExpandTopicIdRef = useRef<string | null>(null);
    showPinDialogForTopicRef.current = showPinDialogForTopic;
    pendingExpandTopicIdRef.current = pendingExpandTopicId;

    // Handle topic selection with lock check
    const handleTopicSelect = (topic: Topic) => {
        if (isTopicLocked(topic)) {
            if (pinStatus?.isSessionValid) {
                setUnlockedTopicIds(prev => new Set(prev).add(topic.id));
                onTopicSelect?.(topic.id);
            } else {
                setShowPinDialogForTopic(topic.id);
            }
            return;
        }
        onTopicSelect?.(topic.id);
    };

    const handleTopicPinSuccess = useCallback(() => {
        const topicIdForSelect = showPinDialogForTopicRef.current;
        const topicIdForExpand = pendingExpandTopicIdRef.current;
        queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
        if (topicIdForSelect) {
            setUnlockedTopicIds(prev => {
                const next = new Set(prev);
                next.add(topicIdForSelect);
                return next;
            });
            onTopicSelect?.(topicIdForSelect);
            setShowPinDialogForTopic(null);
            showPinDialogForTopicRef.current = null;
        }
        if (topicIdForExpand) {
            setUnlockedTopicIds(prev => {
                const next = new Set(prev);
                next.add(topicIdForExpand);
                return next;
            });
            toggleExpanded(topicIdForExpand);
            setPendingExpandTopicId(null);
            pendingExpandTopicIdRef.current = null;
        }
    }, [onTopicSelect, queryClient, toggleExpanded]);

    const handleExpandTopicWithPin = (topic: Topic) => {
        if (isTopicLocked(topic)) {
            if (pinStatus?.isSessionValid) {
                setUnlockedTopicIds(prev => new Set(prev).add(topic.id));
                toggleExpanded(topic.id);
            } else {
                setPendingExpandTopicId(topic.id);
            }
            return;
        }
        toggleExpanded(topic.id);
    };

    const handleStartChatWithTopic = (topic: Topic) => {
        if (isTopicLocked(topic)) {
            if (pinStatus?.isSessionValid) {
                setUnlockedTopicIds(prev => new Set(prev).add(topic.id));
                setActiveChatContext({ spaceId, topicId: topic.id });
                setLocation(`/chat?spaceId=${spaceId}&topicId=${topic.id}`);
            } else {
                setPendingTopicIdForChat(topic.id);
            }
            return;
        }
        setActiveChatContext({ spaceId, topicId: topic.id });
        setLocation(`/chat?spaceId=${spaceId}&topicId=${topic.id}`);
    };

    const handleTopicChatPinSuccess = () => {
        if (pendingTopicIdForChat) {
            queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
            setActiveChatContext({ spaceId, topicId: pendingTopicIdForChat });
            setLocation(`/chat?spaceId=${spaceId}&topicId=${pendingTopicIdForChat}`);
            setPendingTopicIdForChat(null);
        }
    };

    // Session-only unlock: when user leaves, unlockedTopicIds is lost on unmount so topics
    // appear locked again on next visit. No API call needed (topics stay locked in DB).

    if (isLoading) {
        return (
            <div className="space-y-3">
                <div className="h-12 bg-muted/50 animate-pulse rounded-lg"/>
                <div className="h-12 bg-muted/50 animate-pulse rounded-lg"/>
                <div className="h-12 bg-muted/50 animate-pulse rounded-lg"/>
            </div>
        );
    }

    // 100% of parent width only - no pixel maxWidth so layout stays responsive below 885px
    const contentStyle = { width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' as const };

    const renderTopicCard = (topic: Topic): JSX.Element => (
        <TopicCard
            key={topic.id}
            topic={topic}
            refreshKnowledge={refreshKnowledge}
            spaceId={spaceId}
            topics={topics}
            isExpanded={expandedTopics.has(String(topic.id))}
            isSelected={selectedTopicId === topic.id}
            isUnlockedInSession={unlockedTopicIds.has(topic.id)}
            onToggleExpand={() => toggleExpanded(topic.id)}
            onExpandRequested={() => handleExpandTopicWithPin(topic)}
            onSelect={() => handleTopicSelect(topic)}
            onEdit={() => handleOpenEditDialog(topic)}
            onDelete={() => handleDeleteTopic(topic)}
            onLink={() => setTopicToLink(topic)}
            onAddSubtopic={() => handleOpenSubtopicDialog(topic)}
            onShare={() => setTopicToShare(topic)}
            onStartChat={() => handleStartChatWithTopic(topic)}
            onRenameThread={(thread) => {
                setRenameValue(thread.title || "");
                setThreadToRename(thread);
            }}
            onLinkThread={(thread) => setThreadToLink(thread)}
            onLockThread={handleThreadLockClick}
            onDeleteThread={(thread) => setThreadToDelete(thread)}
            toggleLockPending={toggleThreadLockMutation.isPending}
            allThreads={allThreadsProp}
            allDocuments={allDocumentsProp}
            onDocumentClick={handleDocumentClickFromTopics}
            spaceMemberRole={spaceMemberRole}
            spaceOwnerUserId={spaceOwnerUserId}
            itemOverrides={itemOverrides}
            isSharedSpace={isSharedSpace}
            childTopics={getChildTopics(topic.id)}
            renderTopicCard={renderTopicCard}
        />
    );

    return (
        <div className="space-y-3 min-w-0 w-full max-w-full" style={contentStyle}>
            {/* DS EDIT 5aug26 — repacked header: title + icon search/sort/views + add on ONE row */}
            <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                <h3 className="font-semibold whitespace-nowrap truncate min-w-0">Topics & Organization</h3>
                <div className="flex flex-1 items-center justify-end gap-1 min-w-0">
                    {topics.length > 0 && (
                        <>
                            <IconSearch
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search topics..."
                                testId="input-search-topics"
                            />
                            <IconSelectMenu
                                label="Sort topics"
                                value={`${sortField}-${sortDirection}`}
                                onValueChange={(value) => {
                                    const [field, direction] = value.split("-") as [TopicSortField, SortDirection];
                                    setSortField(field);
                                    setSortDirection(direction);
                                }}
                                testId="select-sort-topics"
                                options={[
                                    {value: "name-asc", label: "A → Z", icon: ArrowUp, testId: "sort-topics-name-asc"},
                                    {value: "name-desc", label: "Z → A", icon: ArrowDown, testId: "sort-topics-name-desc"},
                                    {value: "createdAt-desc", label: "Newest", icon: ArrowDown, testId: "sort-topics-created-desc"},
                                    {value: "createdAt-asc", label: "Oldest", icon: ArrowUp, testId: "sort-topics-created-asc"},
                                    {value: "updatedAt-desc", label: "Recent", icon: ArrowDown, testId: "sort-topics-updated-desc"},
                                    {value: "updatedAt-asc", label: "Least recent", icon: ArrowUp, testId: "sort-topics-updated-asc"},
                                ]}
                            />
                            <div className="flex items-center gap-0.5 border rounded-md p-0.5 shrink-0">
                                <Button
                                    variant={viewMode === "list" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setViewMode("list")}
                                    data-testid="button-view-topics-list"
                                    title="List view"
                                >
                                    <List className="h-3.5 w-3.5"/>
                                </Button>
                                <Button
                                    variant={viewMode === "grid" ? "secondary" : "ghost"}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setViewMode("grid")}
                                    data-testid="button-view-topics-grid"
                                    title="Grid view"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5"/>
                                </Button>
                            </div>
                        </>
                    )}
                    <Button
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                            if (!canCreateTopics) {
                                toast({
                                    title: "View-only access",
                                    description: "You are a viewer in this space and cannot create topics.",
                                    variant: "destructive",
                                });
                                return;
                            }
                            setIsCreateDialogOpen(true);
                        }}
                        disabled={!canCreateTopics}
                        data-testid="button-add-topic"
                    >
                        <Plus className="w-4 h-4"/>
                        <span className="hidden sm:inline ml-1">Add Topic</span>
                    </Button>
                </div>
            </div>

            {!canCreateTopics && spaceMemberRole === "viewer" && (
                <Alert variant="destructive">
                    <AlertTitle>View-only access</AlertTitle>
                    <AlertDescription>
                        {isSpaceOwnerUser
                            ? "Your role in this space is viewer. You can browse topics but cannot create new ones."
                            : "This space belongs to another member. You have viewer access and cannot create topics here."}
                    </AlertDescription>
                </Alert>
            )}

            {topics.length === 0 ? (
                <Card className="p-8 text-center">
                    <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground"/>
                    <p className="text-muted-foreground mb-4">
                        {canCreateTopics
                            ? "No topics yet. Create your first topic to organize knowledge in this space."
                            : "No topics yet in this space. You have view-only access and cannot create topics here."}
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (!canCreateTopics) {
                                toast({
                                    title: "View-only access",
                                    description: "You are a viewer in this space and cannot create topics.",
                                    variant: "destructive",
                                });
                                return;
                            }
                            setIsCreateDialogOpen(true);
                        }}
                        disabled={!canCreateTopics}
                        data-testid="button-create-first-topic"
                    >
                        <Plus className="w-4 h-4 mr-2"/>
                        Create First Topic
                    </Button>
                </Card>
            ) : !hasAnyFilteredTopic ? (
                <Card className="p-6 text-center">
                    <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground"/>
                    <p className="text-sm text-muted-foreground">
                        No topics match your search "{searchQuery}"
                    </p>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => setSearchQuery("")}
                        data-testid="button-clear-search"
                    >
                        Clear search
                    </Button>
                </Card>
            ) : viewMode === "grid" ? (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredSortedNativeTopics.map(topic => {
                        const {icon: TopicIcon} = getTopicIcon(topic.icon || "folder");
                        const topicActions = getTopicItemActions(topic);
                        return (
                            <Card
                                key={topic.id}
                                className={`p-3 cursor-pointer hover-elevate ${selectedTopicId === topic.id ? 'ring-2 ring-primary' : ''}`}
                                onClick={() => handleTopicSelect(topic)}
                                data-testid={`card-topic-${topic.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div
                                        className="p-2 rounded-md flex-shrink-0"
                                        style={{backgroundColor: topic.color ? `${topic.color}20` : 'var(--muted)'}}
                                    >
                                        {isTopicLocked(topic) && !unlockedTopicIds.has(topic.id) ? (
                                            <Lock className="w-4 h-4 text-amber-500"/>
                                        ) : (
                                            <TopicIcon className="w-4 h-4"
                                                       style={{color: topic.color || 'var(--muted-foreground)'}}/>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-medium text-sm truncate">{topic.name}</h4>
                                            {isTopicLocked(topic) && !unlockedTopicIds.has(topic.id) && (
                                                <Badge variant="outline"
                                                       className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30">
                                                    Locked
                                                </Badge>
                                            )}
                                            {(childTopicsByParentId.get(topic.id)?.length ?? 0) > 0 && (
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 gap-1">
                                                    <Folder className="w-2.5 h-2.5"/>
                                                    {childTopicsByParentId.get(topic.id)!.length}
                                                </Badge>
                                            )}
                                        </div>
                                        {topic.description && (
                                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{topic.description}</p>
                                        )}
                                        {topic.tags && topic.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {topic.tags.slice(0, 3).map((tag, i) => (
                                                    <Badge key={i} variant="secondary"
                                                           className="text-[10px] px-1.5 py-0">
                                                        {tag}
                                                    </Badge>
                                                ))}
                                                {topic.tags.length > 3 && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                        +{topic.tags.length - 3}
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartChatWithTopic(topic);
                                        }}
                                        data-testid={`button-topic-chat-${topic.id}`}
                                    >
                                        <MessageCircle className="w-3 h-3"/>
                                    </Button>
                                    <DocumentLinkerDrawer
                                        topicId={topic.id}
                                        spaceId={spaceId}
                                        trigger={
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                title="Link notes & documents"
                                                data-testid={`button-link-documents-topic-grid-${topic.id}`}
                                            >
                                                <Link2 className="w-3 h-3"/>
                                            </Button>
                                        }
                                    />
                                    <EntityLockButton
                                        entityType="topic"
                                        entityId={topic.id}
                                        isLocked={isTopicLocked(topic) && !unlockedTopicIds.has(topic.id)}
                                        customTitle={isTopicLocked(topic) ? "Unlock Topic" : "Lock Topic"}
                                    />
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                data-testid={`button-topic-menu-grid-${topic.id}`}
                                            >
                                                <MoreVertical className="w-3 h-3"/>
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                            {topicActions.canEdit && (
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenEditDialog(topic);
                                                }}
                                                data-testid={`button-topic-edit-grid-${topic.id}`}
                                            >
                                                <Pencil className="h-4 w-4 mr-2"/>
                                                Edit Topic
                                            </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTopicToLink(topic);
                                                }}
                                                data-testid={`button-topic-link-grid-${topic.id}`}
                                            >
                                                <Link2 className="h-4 w-4 mr-2"/>
                                                Link Space/Topic
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTopicToShare(topic);
                                                }}
                                                data-testid={`button-share-topic-grid-${topic.id}`}
                                            >
                                                <Share2 className="h-4 w-4 mr-2"/>
                                                Share Topic
                                            </DropdownMenuItem>
                                            {/* <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setTopicToMove(topic);
                        }}
                        data-testid={`button-topic-move-grid-${topic.id}`}
                      >
                        <FolderOutput className="h-4 w-4 mr-2" />
                        Move to Space
                      </DropdownMenuItem> */}
                                            {topicActions.canDelete && (
                                            <>
                                            <DropdownMenuSeparator/>
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteTopic(topic);
                                                }}
                                                className="text-destructive focus:text-destructive"
                                                data-testid={`button-topic-delete-grid-${topic.id}`}
                                            >
                                                <Trash2 className="h-4 w-4 mr-2"/>
                                                Delete Topic
                                            </DropdownMenuItem>
                                            </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </Card>
                        );
                    })}
                </div>
                <SpaceMergedSection title="Merged topics" className="min-w-0" itemCount={filteredSortedMergedTopics.length}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredSortedMergedTopics.map((topic) => {
                            const { icon: TopicIcon } = getTopicIcon(topic.icon || "folder");
                            const topicActions = getTopicItemActions(topic);
                            return (
                                <MergedRowChrome key={topic.id} merged showBadge>
                                    <Card
                                        className={`p-3 cursor-pointer hover-elevate ${selectedTopicId === topic.id ? "ring-2 ring-primary" : ""}`}
                                        onClick={() => handleTopicSelect(topic)}
                                        data-testid={`card-topic-${topic.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div
                                                className="p-2 rounded-md flex-shrink-0"
                                                style={{
                                                    backgroundColor: topic.color ? `${topic.color}20` : "var(--muted)",
                                                }}
                                            >
                                                {isTopicLocked(topic) && !unlockedTopicIds.has(topic.id) ? (
                                                    <Lock className="w-4 h-4 text-amber-500" />
                                                ) : (
                                                    <TopicIcon
                                                        className="w-4 h-4"
                                                        style={{ color: topic.color || "var(--muted-foreground)" }}
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-medium text-sm truncate">{topic.name}</h4>
                                                    {isTopicLocked(topic) && !unlockedTopicIds.has(topic.id) && (
                                                        <Badge
                                                            variant="outline"
                                                            className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30"
                                                        >
                                                            Locked
                                                        </Badge>
                                                    )}
                                                    {(childTopicsByParentId.get(topic.id)?.length ?? 0) > 0 && (
                                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 gap-1">
                                                            <Folder className="w-2.5 h-2.5"/>
                                                            {childTopicsByParentId.get(topic.id)!.length}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {topic.description && (
                                                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                                        {topic.description}
                                                    </p>
                                                )}
                                                {topic.tags && topic.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {topic.tags.slice(0, 3).map((tag, i) => (
                                                            <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                        {topic.tags.length > 3 && (
                                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                                +{topic.tags.length - 3}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStartChatWithTopic(topic);
                                                }}
                                                data-testid={`button-topic-chat-${topic.id}`}
                                            >
                                                <MessageCircle className="w-3 h-3" />
                                            </Button>
                                            <DocumentLinkerDrawer
                                                topicId={topic.id}
                                                spaceId={spaceId}
                                                trigger={
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6"
                                                        title="Link notes & documents"
                                                        data-testid={`button-link-documents-topic-grid-${topic.id}`}
                                                    >
                                                        <Link2 className="w-3 h-3" />
                                                    </Button>
                                                }
                                            />
                                            <EntityLockButton
                                                entityType="topic"
                                                entityId={topic.id}
                                                isLocked={isTopicLocked(topic) && !unlockedTopicIds.has(topic.id)}
                                                customTitle={isTopicLocked(topic) ? "Unlock Topic" : "Lock Topic"}
                                            />
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6"
                                                        data-testid={`button-topic-menu-grid-${topic.id}`}
                                                    >
                                                        <MoreVertical className="w-3 h-3" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-48">
                                                    {topicActions.canEdit && (
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEditDialog(topic);
                                                        }}
                                                        data-testid={`button-topic-edit-grid-${topic.id}`}
                                                    >
                                                        <Pencil className="h-4 w-4 mr-2" />
                                                        Edit Topic
                                                    </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setTopicToLink(topic);
                                                        }}
                                                        data-testid={`button-topic-link-grid-${topic.id}`}
                                                    >
                                                        <Link2 className="h-4 w-4 mr-2" />
                                                        Link Space/Topic
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setTopicToShare(topic);
                                                        }}
                                                        data-testid={`button-share-topic-grid-${topic.id}`}
                                                    >
                                                        <Share2 className="h-4 w-4 mr-2" />
                                                        Share Topic
                                                    </DropdownMenuItem>
                                                    {topicActions.canDelete && (
                                                    <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteTopic(topic);
                                                        }}
                                                        className="text-destructive focus:text-destructive"
                                                        data-testid={`button-topic-delete-grid-${topic.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Delete Topic
                                                    </DropdownMenuItem>
                                                    </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </Card>
                                </MergedRowChrome>
                            );
                        })}
                    </div>
                </SpaceMergedSection>
                </>
            ) : (
                <div className="space-y-2 w-full min-w-0 max-w-full overflow-hidden flex-1 min-h-0">
                    {filteredSortedNativeTopics.map(topic => renderTopicCard(topic))}
                    <SpaceMergedSection title="Merged topics" className="min-w-0" itemCount={filteredSortedMergedTopics.length}>
                        {filteredSortedMergedTopics.map((topic) => (
                            <MergedRowChrome key={topic.id} merged showBadge>
                                {renderTopicCard(topic)}
                            </MergedRowChrome>
                        ))}
                    </SpaceMergedSection>
                </div>
            )}

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{parentTopicForCreate ? "Create Subtopic" : "Create New Topic"}</DialogTitle>
                        <DialogDescription>
                            {parentTopicForCreate
                                ? `Add a subtopic nested under "${parentTopicForCreate.name}".`
                                : "Add a new topic to organize knowledge within this space."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="topic-name">Name *</Label>
                            <Input
                                id="topic-name"
                                placeholder="e.g., Research Methods, Key Concepts"
                                value={newTopicName}
                                onChange={(e) => setNewTopicName(e.target.value)}
                                data-testid="input-topic-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="topic-description">Description</Label>
                            <Textarea
                                id="topic-description"
                                placeholder="Brief description of what this topic covers..."
                                value={newTopicDescription}
                                onChange={(e) => setNewTopicDescription(e.target.value)}
                                data-testid="input-topic-description"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Choose an Icon</Label>
                            <div className="grid grid-cols-5 gap-2">
                                {topicIconOptions.map((option) => {
                                    const Icon = option.icon;
                                    return (
                                        <button
                                            key={option.name}
                                            type="button"
                                            onClick={() => setNewTopicIcon(option.name)}
                                            className={`p-3 rounded-md border-2 transition-all ${
                                                newTopicIcon === option.name
                                                    ? "border-primary bg-primary/10"
                                                    : "border-transparent bg-accent hover-elevate"
                                            }`}
                                            data-testid={`icon-topic-${option.name}`}
                                        >
                                            <Icon className={`h-5 w-5 mx-auto ${option.color}`}/>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="topic-tags">Tags (comma-separated)</Label>
                            <Input
                                id="topic-tags"
                                placeholder="e.g., research, methodology, analysis"
                                value={newTopicTags}
                                onChange={(e) => setNewTopicTags(e.target.value)}
                                data-testid="input-topic-tags"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCloseCreateDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateTopic}
                            disabled={createTopicMutation.isPending}
                            data-testid="button-confirm-create-topic"
                        >
                            {createTopicMutation.isPending ? "Creating..." : parentTopicForCreate ? "Create Subtopic" : "Create Topic"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Topic</DialogTitle>
                        <DialogDescription>
                            Update the topic details.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-topic-name">Name *</Label>
                            <Input
                                id="edit-topic-name"
                                placeholder="Topic name"
                                value={newTopicName}
                                onChange={(e) => setNewTopicName(e.target.value)}
                                data-testid="input-edit-topic-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-topic-description">Description</Label>
                            <Textarea
                                id="edit-topic-description"
                                placeholder="Brief description..."
                                value={newTopicDescription}
                                onChange={(e) => setNewTopicDescription(e.target.value)}
                                data-testid="input-edit-topic-description"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Choose an Icon</Label>
                            <div className="grid grid-cols-5 gap-2">
                                {topicIconOptions.map((option) => {
                                    const Icon = option.icon;
                                    return (
                                        <button
                                            key={option.name}
                                            type="button"
                                            onClick={() => setNewTopicIcon(option.name)}
                                            className={`p-3 rounded-md border-2 transition-all ${
                                                newTopicIcon === option.name
                                                    ? "border-primary bg-primary/10"
                                                    : "border-transparent bg-accent hover-elevate"
                                            }`}
                                            data-testid={`icon-edit-topic-${option.name}`}
                                        >
                                            <Icon className={`h-5 w-5 mx-auto ${option.color}`}/>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-topic-tags">Tags (comma-separated)</Label>
                            <Input
                                id="edit-topic-tags"
                                placeholder="tag1, tag2, tag3"
                                value={newTopicTags}
                                onChange={(e) => setNewTopicTags(e.target.value)}
                                data-testid="input-edit-topic-tags"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCloseEditDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateTopic}
                            disabled={updateTopicMutation.isPending}
                            data-testid="button-confirm-edit-topic"
                        >
                            {updateTopicMutation.isPending ? "Saving..." : "Save Changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Topic?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete "{deletingTopic?.name}" and all its subtopics.
                            Threads linked to this topic will be unlinked but not deleted.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteTopic}
                            disabled={deleteTopicMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete-topic"
                        >
                            {deleteTopicMutation.isPending ? "Deleting..." : "Delete Topic"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <PinVerificationDialog
                open={showPinDialogForTopic !== null || pendingExpandTopicId !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowPinDialogForTopic(null);
                        setPendingExpandTopicId(null);
                        showPinDialogForTopicRef.current = null;
                        pendingExpandTopicIdRef.current = null;
                    }
                }}
                onSuccess={handleTopicPinSuccess}
                title="Unlock Topic"
                description={pendingExpandTopicId ? "Enter your PIN to expand this topic." : "This topic is locked. Enter your PIN to view its contents."}
            />

            <PinVerificationDialog
                open={!!pendingTopicIdForChat}
                onOpenChange={(open) => !open && setPendingTopicIdForChat(null)}
                onSuccess={handleTopicChatPinSuccess}
                title="Unlock Topic"
                description="Enter your PIN to open chat with this locked topic."
            />

            {/* Thread Rename Dialog */}
            <Dialog open={!!threadToRename} onOpenChange={(open) => !open && setThreadToRename(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename Thread</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="thread-rename-input">Thread Name</Label>
                        <Input
                            id="thread-rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            placeholder="Enter thread name..."
                            className="mt-2"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && renameValue.trim() && threadToRename) {
                                    renameThreadMutation.mutate({
                                        threadId: threadToRename.id,
                                        title: renameValue.trim()
                                    });
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setThreadToRename(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (threadToRename && renameValue.trim()) {
                                    renameThreadMutation.mutate({
                                        threadId: threadToRename.id,
                                        title: renameValue.trim()
                                    });
                                }
                            }}
                            disabled={!renameValue.trim() || renameThreadMutation.isPending}
                        >
                            {renameThreadMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Thread Linking Dialog */}
            {threadToLink && (
                <ThreadLinkingDialog
                    open={!!threadToLink}
                    onOpenChange={(open) => !open && setThreadToLink(null)}
                    linkedSpaceIds={threadToLink.linkedSpaceIds || []}
                    topicId={threadToLink.topicId || null}
                    tags={threadToLink.tags || []}
                    onUpdateSpaces={(spaceIds) => {
                        updateThreadSpacesMutation.mutate({threadId: threadToLink.id, spaceIds});
                    }}
                    onUpdateTopic={(topicId) => {
                        updateThreadTopicMutation.mutate({threadId: threadToLink.id, topicId});
                    }}
                    onUpdateTags={(tags) => {
                        updateThreadTagsMutation.mutate({threadId: threadToLink.id, tags});
                    }}
                    editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace ? spaceId : null}
                />
            )}

            {/* Topic Linking Dialog */}
            {topicToLink && (
                <ThreadLinkingDialog
                    open={!!topicToLink}
                    onOpenChange={(open) => !open && setTopicToLink(null)}
                    title="Link Space/Topic"
                    description="Connect this topic to spaces, topics, and tags for better organization"
                    linkedSpaceIds={topicLinkedSpaceIds ?? [topicToLink.spaceId]}
                    requiredSpaceIds={topicLinkRequiredSpaceIds}
                    topicId={null}
                    tags={[]}
                    editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace ? spaceId : null}
                    onUpdateSpaces={(ids) => {
                        syncTopicLinkedSpacesMutation.mutate({
                            topicId: topicToLink.id,
                            spaceIds: ids,
                        });
                    }}
                    onUpdateTopic={(selectedTopicId) => {
                        // Show related topics
                        toast({
                            title: "Related Topics",
                            description: "Topic relationships coming soon!",
                        });
                    }}
                    onUpdateTags={(tags) => {
                        // Future: Could add tags field to topics schema
                        toast({
                            title: "Topic Tags",
                            description: "Topic tagging coming soon!",
                        });
                    }}
                />
            )}

            <ShareTopicDialog
                open={!!topicToShare}
                onOpenChange={(open) => !open && setTopicToShare(null)}
                topicId={topicToShare?.id ?? ""}
                topicName={topicToShare?.name ?? "Untitled Topic"}
            />

            {/* Thread PIN Verification Dialog */}
            <PinVerificationDialog
                open={isPinDialogOpen}
                onOpenChange={setIsPinDialogOpen}
                onSuccess={() => {
                    if (threadToLock) {
                        const shouldLock = !threadToLock.isLocked;
                        toggleThreadLockMutation.mutate({threadId: threadToLock.id, lock: shouldLock});
                    }
                }}
                title={threadToLock?.isLocked ? "Unlock Thread" : "Lock Thread"}
                description="Enter your PIN to continue"
            />

            {/* Delete Thread Confirmation Dialog */}
            <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Thread?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{threadToDelete?.title || "this thread"}"?
                            This will permanently delete the thread and all its messages.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (threadToDelete) {
                                    deleteThreadMutation.mutate(threadToDelete.id);
                                }
                            }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete-thread"
                        >
                            {deleteThreadMutation.isPending ? "Deleting..." : "Delete Thread"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Move Topic to Space Dialog */}
            <Dialog open={!!topicToMove} onOpenChange={(open) => !open && setTopicToMove(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Move Topic to Another Space</DialogTitle>
                        <DialogDescription>
                            Select a space to move "{topicToMove?.name}" to. This will relocate all associated content.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Target Space</Label>
                            <Select
                                value={selectedTargetSpaceId}
                                onValueChange={setSelectedTargetSpaceId}
                            >
                                <SelectTrigger data-testid="select-target-space">
                                    <SelectValue placeholder="Select a space..."/>
                                </SelectTrigger>
                                <SelectContent>
                                    {allSpaces
                                        .filter(s => s.id !== spaceId)
                                        .map((space) => (
                                            <SelectItem key={space.id} value={space.id}
                                                        data-testid={`option-space-${space.id}`}>
                                                {space.name}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTopicToMove(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (topicToMove && selectedTargetSpaceId) {
                                    moveTopicMutation.mutate({
                                        topicId: topicToMove.id,
                                        targetSpaceId: selectedTargetSpaceId
                                    });
                                }
                            }}
                            disabled={!selectedTargetSpaceId || moveTopicMutation.isPending}
                            data-testid="button-confirm-move-topic"
                        >
                            {moveTopicMutation.isPending ? "Moving..." : "Move Topic"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Document unlock (for linked docs in topic accordion) */}
            <PinVerificationDialog
                open={showPinDialogForDoc}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowPinDialogForDoc(false);
                        setPendingDocumentIdToOpen(null);
                    }
                }}
                onSuccess={handleDocPinSuccessForTopics}
                title="Unlock Document"
                description="This document is locked. Enter your PIN to view its contents."
            />

            <DocumentViewerModal
                open={!!documentToView}
                onOpenChange={(open) => { if (!open) setDocumentToView(null); }}
                document={documentToView}
            />
        </div>
    );
}

interface TopicCardProps {
    topic: Topic;
    spaceId: string;
    topics: Topic[];
    isExpanded: boolean;
    isSelected: boolean;
    isUnlockedInSession?: boolean;
    onToggleExpand: () => void;
    /** When topic is locked and user clicks expand, call this to show PIN instead of expanding */
    onExpandRequested?: () => void;
    onSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onLink: () => void;
    onAddSubtopic?: () => void;
    onShare?: () => void;
    onStartChat: () => void;
    onRenameThread: (thread: ChatThread) => void;
    onLinkThread: (thread: ChatThread) => void;
    onLockThread: (thread: ChatThread) => void;
    onDeleteThread: (thread: ChatThread) => void;
    toggleLockPending?: boolean;
    allThreads?: ChatThread[];
    allDocuments?: KnowledgeBaseItem[];
    refreshKnowledge?: () => void;
    /** When provided, linked documents use this (e.g. open read-only modal for shared docs) */
    onDocumentClick?: (documentId: string, isLocked?: boolean) => void;
    spaceMemberRole?: SpaceMemberRole | null;
    spaceOwnerUserId?: string | null;
    itemOverrides?: ItemOverrideMap;
    isSharedSpace?: boolean;
    childTopics?: Topic[];
    renderTopicCard?: (topic: Topic) => React.ReactNode;
}
/** DS EDIT 5aug26 — JS media query so narrow screens get ONE combined topic menu
 * (CSS-hidden menu items would break Radix keyboard navigation). */
function useIsNarrowViewport(maxWidth = 480) {
    const [narrow, setNarrow] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
        const update = () => setNarrow(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, [maxWidth]);
    return narrow;
}

function TopicCard({
                       topic,
                       spaceId,
                       topics,
                       isExpanded,
                       refreshKnowledge,
                       isSelected,
                       isUnlockedInSession,
                       onToggleExpand,
                       onExpandRequested,
                       onSelect,
                       onEdit,
                       onDelete,
                       onLink,
                       onAddSubtopic,
                       onShare,
                       onStartChat,
                       onRenameThread,
                       onLinkThread,
                       onLockThread,
                       onDeleteThread,
                       toggleLockPending,
                       allThreads,
                       allDocuments,
                       onDocumentClick: onDocumentClickProp,
                       spaceMemberRole = null,
                       spaceOwnerUserId = null,
                       itemOverrides,
                       isSharedSpace = false,
                       childTopics = [],
                       renderTopicCard,
                   }: TopicCardProps) {
    const [subtopicsExpanded, setSubtopicsExpanded] = useState(false);
    const [, setLocation] = useLocation();
    const [threadSearchQuery, setThreadSearchQuery] = useState("");
    const [topicDocumentModalOpen, setTopicDocumentModalOpen] = useState(false);
    const [createDocDialogOpen, setCreateDocDialogOpen] = useState(false);
    const [newDocTitle, setNewDocTitle] = useState("");
    const [newDocContent, setNewDocContent] = useState("");
    const {setSelectedDocumentId} = useLibrary();
    const {toast} = useToast();
    const queryClient = useQueryClient();
    const { currentWorkspaceId } = useWorkspace();
    const { user } = useAuth();

    const topicActions = effectiveItemActions(
        spaceMemberRole,
        "topic",
        topic.id,
        topic.createdByUserId,
        user?.id,
        spaceOwnerUserId,
        itemOverrides
    );

    const refetchKnowledgeForTopic = useCallback(async () => {
        await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false });
        refreshKnowledge?.();
    }, [queryClient, refreshKnowledge]);

    const createDocumentMutation = useMutation({
        mutationFn: async () => {
            return await knowledgeBaseService.createKnowledgeBaseItem({
                title: newDocTitle || "Untitled Document",
                content: newDocContent,
                type: "note",
                spaceId: spaceId || null,
                topicIds: [topic.id],
                tagIds: [],
                folderId: null,
                googleDocsId: null,
                metadata: {},
            }, currentWorkspaceId ?? null);
        },
        onSuccess: () => {
            setCreateDocDialogOpen(false);
            setNewDocTitle("");
            setNewDocContent("");
            refetchKnowledgeForTopic();
            toast({ title: "Document created", description: "Document has been created and linked to this topic." });
        },
        onError: (err: unknown) => {
            toast({ title: "Creation failed", description: err instanceof Error ? err.message : "Could not create document", variant: "destructive" });
        },
    });

    // Use allThreads from prop if provided, otherwise fetch (for backward compatibility)
    /*  const { data: allThreadsFromQuery = [] } = useQuery<ChatThread[]>({
        queryKey: ["/api/chat-thread-summaries"],
        queryFn: async () => {
          return await chatThreadsService.getThreadSummaries();
        },
        enabled: (isExpanded || !!topic.id) && allThreadsProp === undefined,
        staleTime: 30000, // Cache for 30 seconds to prevent unnecessary refetches
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
      });

      const allThreads = (allThreadsProp ?? allThreadsFromQuery) as ChatThread[];*/

    // Helper function to get topicIds from thread (supports both array and single value for backward compatibility)
    const getThreadTopicIds = useCallback((thread: any): string[] => {
        // Check for topicIds array first (new format)
        if (Array.isArray(thread.topicIds)) {
            return thread.topicIds.filter((id: any) => id != null).map((id: any) => String(id));
        }
        
        // Fallback to single topicId (backward compatibility)
        if (thread.topicId) {
            return [String(thread.topicId)];
        }
        
        return [];
    }, []);

    // Helper function to link a thread to a topic (handles migration from old format to new format)
    const linkThreadToTopic = useCallback(async (thread: any, topicId: string) => {
        const currentTopicIds = getThreadTopicIds(thread);
        const topicIdStr = String(topicId);
        
        // If already linked, don't do anything
        if (currentTopicIds.some(id => String(id) === topicIdStr)) {
            return;
        }
        
        // Add the new topicId to the array
        const updatedTopicIds = [...currentTopicIds, topicIdStr];

        // Get workspaceId from thread or use currentWorkspaceId (threads can be personal or workspace-scoped)
        // Thread might have workspaceId, workspace_id, or neither (personal thread)
        const threadWorkspaceId = (thread as any).workspaceId ?? (thread as any).workspace_id ?? currentWorkspaceId ?? null;

        try {
            // Update using the new topicIds array format and capture the returned thread
            const updatedThread = await chatThreadsService.updateChatThread(
                thread.id, 
                {topicIds: updatedTopicIds} as any,
                threadWorkspaceId,
                spaceId
            );

            if (!updatedThread) {
                console.error("Failed to update thread with topicIds", {
                    threadId: thread.id,
                    workspaceId: threadWorkspaceId,
                    updatedTopicIds,
                    thread
                });
                toast({
                    title: "Failed to link thread",
                    description: "The thread could not be updated. It may not exist or you may not have permission.",
                    variant: "destructive"
                });
                return;
            }

            // Get the actual topicIds from the returned thread (in case server normalized it)
        // The returned thread uses the same structure as mapToThread: topicIds is array or undefined
        const finalTopicIds = updatedThread.topicIds && Array.isArray(updatedThread.topicIds) 
            ? updatedThread.topicIds 
            : getThreadTopicIds(updatedThread);
        
        // Update the cache with the actual returned thread data to ensure consistency
        // Use the exact structure from the returned thread to match what the server expects
        queryClient.setQueriesData<any[]>(
            {queryKey: ["/api/chat-thread-summaries"], exact: false},
            (oldData) => {
                if (!oldData) return oldData;
                return oldData.map(t => {
                    if (t.id === thread.id) {
                        return {
                            ...t,
                            // Use undefined (not null) when empty to match mapToThreadSummary behavior
                            topicIds: finalTopicIds.length > 0 ? finalTopicIds : undefined,
                            topicId: null // Clear old topicId when using topicIds array
                        };
                    }
                    return t;
                });
            }
        );

        // Also update the full thread cache
        queryClient.setQueriesData<any>(
            {queryKey: ["/api/chat-threads"], exact: false},
            (oldData) => {
                if (!oldData) return oldData;
                if (Array.isArray(oldData)) {
                    return oldData.map(t => {
                        if (t.id === thread.id) {
                            return {
                                ...t,
                                // Use undefined (not null) when empty to match mapToThread behavior
                                topicIds: finalTopicIds.length > 0 ? finalTopicIds : undefined,
                                topicId: null
                            };
                        }
                        return t;
                    });
                } else if (oldData.id === thread.id) {
                    return {
                        ...oldData,
                        topicIds: finalTopicIds.length > 0 ? finalTopicIds : undefined,
                        topicId: null
                    };
                }
                return oldData;
            }
        );
        
        // Invalidate and refetch queries to ensure all components are in sync
        // Use invalidateQueries instead of refetchQueries to avoid race conditions
        queryClient.invalidateQueries({queryKey: ["/api/chat-thread-summaries"], exact: false});
        queryClient.invalidateQueries({queryKey: ["/api/chat-threads"], exact: false});
        queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics", topicId, "threads"]});
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
        } catch (error: any) {
            console.error("Error linking thread to topic:", error);
            toast({
                title: "Failed to link thread",
                description: error?.message || "An error occurred while linking the thread to the topic.",
                variant: "destructive"
            });
        }
    }, [getThreadTopicIds, queryClient, spaceId, currentWorkspaceId, toast]);

    // Filter threads by topicId/topicIds - threads that match this topic's id
    const threads = useMemo(() => {
        const topicIdStr = String(topic.id);
        return allThreads.filter(thread => {
            const threadTopicIds = getThreadTopicIds(thread);
            return threadTopicIds.some(tid => String(tid) === topicIdStr);
        });
    }, [allThreads, topic.id, getThreadTopicIds]);

    // Get threads not linked to this topic (for the link dropdown)
    const availableThreadsToLink = useMemo(() => {
        const topicIdStr = String(topic.id);
        const filtered = allThreads.filter(thread => {
            const threadTopicIds = getThreadTopicIds(thread);
            return !threadTopicIds.some(tid => String(tid) === topicIdStr);
        });
        if (!threadSearchQuery.trim()) return filtered;
        const query = threadSearchQuery.toLowerCase();
        return filtered.filter(thread =>
            (thread.title || "Untitled Thread").toLowerCase().includes(query)
        );
    }, [allThreads, topic.id, threadSearchQuery, getThreadTopicIds]);

    // All threads linked to this topic (threads with matching topicId or topicIds)
    const allLinkedThreads = useMemo(() => {
        const topicIdStr = String(topic.id);
        return allThreads.filter(thread => {
            const threadTopicIds = getThreadTopicIds(thread);
            return threadTopicIds.some(tid => String(tid) === topicIdStr);
        });
    }, [allThreads, topic.id, getThreadTopicIds]);

    // Thread embedding status for RAG (embed / include in RAG UI)
    const linkedThreadIds = useMemo(() => allLinkedThreads.map(t => t.id), [allLinkedThreads]);
    const {data: threadEmbeddingStatusMap = {}} = useQuery<Record<string, boolean>>({
        queryKey: ["thread-embedding-status", linkedThreadIds.slice().sort().join(",")],
        queryFn: () => ragService.getThreadEmbeddingStatusForThreads(linkedThreadIds),
        enabled: isExpanded && linkedThreadIds.length > 0,
        staleTime: 30000,
    });

    // Helper function to get topicIds from document (supports both array and single value for backward compatibility)
    const getDocumentTopicIds = useCallback((doc: KnowledgeBaseItem): string[] => {
        const metadata = doc.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        // Check for topicIds array first (new format)
        if (Array.isArray(parsedMetadata.topicIds)) {
            return parsedMetadata.topicIds.map((id: any) => String(id)).filter((id: string) => id !== 'null' && id !== 'undefined');
        }
        
        // Fallback to single topicId in metadata (backward compatibility - more reliable than doc.topicId)
        if (parsedMetadata.topicId) {
            return [String(parsedMetadata.topicId)];
        }

        if (Array.isArray(doc.topicIds)) {
            return doc.topicIds.map((id: unknown) => String(id)).filter((id: string) => id !== "null" && id !== "undefined");
        }
        if (doc.topicId) {
            return [String(doc.topicId)];
        }

        return [];
    }, []);

    // Use allDocuments from prop - should always be provided from MastermindSpaceDetail
    // Memoize filtered documents to prevent unnecessary recalculations
    // Support both topicIds array (new) and topicId single value (backward compatibility)
    const linkedDocuments = useMemo(() => {
        if (!allDocuments || !allDocuments.length) return [];
        const topicIdStr = String(topic.id);
        return allDocuments.filter(doc => {
            const docTopicIds = getDocumentTopicIds(doc);
            return docTopicIds.some(id => String(id) === topicIdStr);
        });
    }, [allDocuments, topic.id, getDocumentTopicIds]);

    const threadCount = useMemo(() => threads?.length || 0, [threads]);
    const documentCount = useMemo(() => linkedDocuments.length, [linkedDocuments]);
    const isTopicLockedHere = topic?.isLocked === true || topic?.isLocked === "true";
    const lockedNoAccess = isTopicLockedHere && !isUnlockedInSession;
    const isNarrow = useIsNarrowViewport();


    return (
        <div className="w-full min-w-0 max-w-full">
        <Card
            className={`overflow-hidden transition-colors w-full min-w-0 max-w-full ${
                isSelected ? "ring-2 ring-primary" : ""
            }`}
            data-testid={`card-topic-${topic.id}`}
        >
            <div
                className="flex items-center gap-2 sm:gap-3 p-3 hover-elevate cursor-pointer min-w-0 w-full max-w-full overflow-hidden"
                onClick={onSelect}
            >
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isTopicLockedHere && !isUnlockedInSession) {
                            onExpandRequested?.();
                        } else {
                            onToggleExpand();
                        }
                    }}
                    data-testid={`button-expand-topic-${topic.id}`}
                >
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4"/>
                    ) : (
                        <ChevronRight className="w-4 h-4"/>
                    )}
                </Button>

                <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                    {(() => {
                        const {icon: Icon, color} = getTopicIcon(topic.icon);
                        if (isTopicLockedHere && !isUnlockedInSession) {
                            return <Lock className="w-5 h-5 flex-shrink-0 text-amber-500"/>;
                        }
                        return <Icon className={`w-5 h-5 flex-shrink-0 ${isExpanded ? "text-primary" : color}`}/>;
                    })()}
                    <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="font-medium truncate min-w-0">{topic.name}</div>
                            {isTopicLockedHere && !isUnlockedInSession && (
                                <Badge variant="outline"
                                       className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-500 border-amber-500/30 max-[480px]:hidden">
                                    Locked
                                </Badge>
                            )}
                        </div>
                        {topic.description && (
                            <p className="text-xs text-muted-foreground truncate max-[480px]:hidden">
                                {topic.description}
                            </p>
                        )}
                    </div>
                </div>

                {/* Inline actions: visible above 480px */}
                <div className="hidden min-[481px]:flex items-center gap-2 flex-shrink-0">
                    {threadCount > 0 && (
                        <Badge variant="secondary" className="gap-1">
                            <MessageSquare className="w-3 h-3"/>
                            {threadCount}
                        </Badge>
                    )}
                    {topic.linkedSourceIds && topic.linkedSourceIds.length > 0 && (
                        <Badge variant="outline" className="gap-1">
                            <Link2 className="w-3 h-3"/>
                            {topic.linkedSourceIds.length}
                        </Badge>
                    )}
                    {documentCount > 0 && (
                        <Badge variant="secondary" className="gap-1">
                            <FileText className="w-3 h-3"/>
                            {documentCount}
                        </Badge>
                    )}
                    <Button
                        variant="default"
                        size="sm"
                        className="h-8 bg-blue-500 hover:bg-blue-600 text-white"
                        disabled={lockedNoAccess}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!lockedNoAccess) onStartChat();
                        }}
                        title={lockedNoAccess ? "Unlock topic to start chat" : "Start AI conversation with this topic"}
                        data-testid={`button-chat-topic-${topic.id}`}
                    >
                        <MessageCircle className="w-4 h-4 mr-1.5"/>
                        Chat
                    </Button>
                    <EntityLockButton
                        entityType="topic"
                        entityId={topic.id}
                        isLocked={isTopicLockedHere && !isUnlockedInSession}
                        size="default"
                        customTitle={isTopicLockedHere ? "Unlock Topic" : "Lock Topic"}
                    />
                </div>

                {/* DS EDIT 5aug26 — the separate "···" actions menu is gone: on narrow screens its
                    quick actions merge into the single ⋮ menu below (combined per operator markup). */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={lockedNoAccess}
                            title={lockedNoAccess ? "Unlock topic first" : undefined}
                            data-testid={`button-topic-menu-${topic.id}`}
                        >
                            <MoreVertical className="h-4 w-4"/>
                        </Button>
                    </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-80">
                            {isNarrow && (
                                <>
                                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                                        {threadCount > 0 && <span>{threadCount} thread{threadCount !== 1 ? "s" : ""}</span>}
                                        {(topic.linkedSourceIds?.length ?? 0) > 0 && (
                                            <span>{threadCount > 0 ? " · " : ""}{topic.linkedSourceIds!.length} links</span>
                                        )}
                                        {documentCount > 0 && (
                                            <span>{(threadCount > 0 || (topic.linkedSourceIds?.length ?? 0) > 0) ? " · " : ""}{documentCount} doc{documentCount !== 1 ? "s" : ""}</span>
                                        )}
                                        {threadCount === 0 && (topic.linkedSourceIds?.length ?? 0) === 0 && documentCount === 0 && (
                                            <span>No links</span>
                                        )}
                                    </div>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStartChat(); }} data-testid={`actions-chat-topic-${topic.id}`}>
                                        <MessageCircle className="h-4 w-4 mr-2"/> Start chat
                                    </DropdownMenuItem>
                                    {availableThreadsToLink.length > 0 && (
                                        <DropdownMenuSub>
                                            <DropdownMenuSubTrigger className="text-xs">Link threads</DropdownMenuSubTrigger>
                                            <DropdownMenuSubContent className="max-h-56 overflow-y-auto">
                                                {availableThreadsToLink.map((thread) => (
                                                    <DropdownMenuItem
                                                        key={thread.id}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                await linkThreadToTopic(thread, topic.id);
                                                            } catch (err) {
                                                                console.error("Failed to link thread to topic:", err);
                                                                toast({
                                                                    title: "Link Failed",
                                                                    description: err instanceof Error ? err.message : "Could not link thread to topic",
                                                                    variant: "destructive"
                                                                });
                                                            }
                                                        }}
                                                        data-testid={`actions-link-thread-${thread.id}`}
                                                    >
                                                        <MessageCircle className="h-3.5 w-3.5 mr-2 shrink-0"/>
                                                        <span className="truncate text-xs block max-w-[140px]">
                                                            {truncateTitle(thread.title || "Untitled Thread", TITLE_MAX_LENGTH)}
                                                        </span>
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuSubContent>
                                        </DropdownMenuSub>
                                    )}
                                    <DocumentLinkerDrawer
                                        topicId={topic.id}
                                        spaceId={spaceId}
                                        trigger={
                                            <button
                                                type="button"
                                                className="flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`actions-link-docs-${topic.id}`}
                                            >
                                                <Link2 className="h-4 w-4"/> Link documents
                                            </button>
                                        }
                                    />
                                    <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                        <EntityLockButton
                                            entityType="topic"
                                            entityId={topic.id}
                                            isLocked={isTopicLockedHere && !isUnlockedInSession}
                                            size="menu"
                                            customTitle={isTopicLockedHere ? "Unlock Topic" : "Lock Topic"}
                                        />
                                    </div>
                                    <DropdownMenuSeparator/>
                                </>
                            )}
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTopicDocumentModalOpen(true);
                                }}
                                data-testid={`menu-add-new-document-topic-${topic.id}`}
                            >
                                <Plus className="h-4 w-4 mr-2"/>
                                Add New Document
                            </DropdownMenuItem>
                            <DropdownMenuSeparator/>
                            {onAddSubtopic && (
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddSubtopic();
                                    }}
                                    data-testid={`menu-add-subtopic-${topic.id}`}
                                >
                                    <Plus className="h-4 w-4 mr-2"/>
                                    Add Subtopic
                                </DropdownMenuItem>
                            )}
                            {topicActions.canEdit && (
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit();
                                }}
                                data-testid={`button-edit-topic-${topic.id}`}
                            >
                                <Pencil className="h-4 w-4 mr-2"/>
                                Rename Topic
                            </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onLink();
                                }}
                                data-testid={`button-link-topic-${topic.id}`}
                            >
                                <Link2 className="h-4 w-4 mr-2"/>
                                Link to Space/Topic
                            </DropdownMenuItem>
                            {onShare && (
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onShare();
                                    }}
                                    data-testid={`button-share-topic-${topic.id}`}
                                >
                                    <Share2 className="h-4 w-4 mr-2"/>
                                    Share Topic
                                </DropdownMenuItem>
                            )}
                            {threadCount > 0 && (
                                <>
                                    <DropdownMenuItem
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                await ragService.embedThreadsForTopic(topic.id);
                                                toast({ title: "Embedded for AI search", description: `All ${threadCount} thread(s) in this topic are now searchable by meaning.` });
                                                queryClient.invalidateQueries({ queryKey: ["thread-embedding-status"] });
                                            } catch (err) {
                                                toast({ title: "Embed failed", description: err instanceof Error ? err.message : "Could not embed threads.", variant: "destructive" });
                                            }
                                        }}
                                        data-testid={`button-embed-all-threads-topic-${topic.id}`}
                                    >
                                        <Brain className="h-4 w-4 mr-2"/> Embed for AI search
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                        className="flex items-center justify-between gap-4 cursor-default"
                                        title="When enabled, threads in this topic can be searched by AI"
                                    >
                                        <span className="flex items-center"><Brain className="h-4 w-4 mr-2"/>Available to AI</span>
                                        <Switch
                                            checked={allLinkedThreads.length > 0 && allLinkedThreads.every((t: { includeInRag?: boolean }) => t.includeInRag !== false)}
                                            onCheckedChange={async (checked) => {
                                                try {
                                                    await chatThreadsService.bulkUpdateThreadsIncludeInRagByTopic(topic.id, checked);
                                                    toast({ title: checked ? "Available to AI" : "Hidden from AI", description: checked ? "All threads in this topic can now be searched by AI." : "All threads in this topic are now excluded from AI search." });
                                                    queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries"] });
                                                    queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
                                                } catch (err) {
                                                    toast({ title: "Update failed", variant: "destructive" });
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`switch-topic-include-all-rag-${topic.id}`}
                                        />
                                    </DropdownMenuItem>
                                </>
                            )}
                            {/* <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onMove();
                }}
                data-testid={`button-move-topic-${topic.id}`}
              >
                <FolderOutput className="h-4 w-4 mr-2" />
                Move to Space
              </DropdownMenuItem> */}
                            {topicActions.canDelete && (
                            <>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                className="text-destructive focus:text-destructive"
                                data-testid={`button-delete-topic-${topic.id}`}
                            >
                                <Trash2 className="h-4 w-4 mr-2"/>
                                Delete Topic
                            </DropdownMenuItem>
                            </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
            </div>

            {isExpanded && !lockedNoAccess && (
                <div className="border-t bg-muted/30 px-3 py-2 w-full min-w-0 overflow-hidden">
                    {topic.tags && topic.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                            {topic.tags.map((tag, index) => (
                                <Badge
                                    key={index}
                                    variant="outline"
                                    className="text-xs"
                                    data-testid={`badge-topic-tag-${topic.id}-${index}`}
                                >
                                    {tag}
                                </Badge>
                            ))}
                        </div>
                    )}

                    <div className="mb-3">
                            <div
                                className="text-xs font-medium text-muted-foreground mb-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3"/>
                                    Threads ({allLinkedThreads.length})
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {/*<Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onLink();
                                        }}
                                        data-testid={`button-link-space-expanded-${topic.id}`}
                                    >
                                        <Link2 className="w-3 h-3 mr-1"/>
                                        Link Space
                                    </Button>*/}
                                    {availableThreadsToLink.length > 0 && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`button-link-threads-to-topic-expanded-${topic.id}`}
                                            >
                                                <Plus className="w-3 h-3 mr-1"/>
                                                Link Thread
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-64 p-0">
                                            <div className="p-2 border-b">
                                                <div className="relative">
                                                    <Search
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"/>
                                                    <Input
                                                        placeholder="Search threads..."
                                                        value={threadSearchQuery}
                                                        onChange={(e) => setThreadSearchQuery(e.target.value)}
                                                        className="pl-7 h-8 text-xs"
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-testid={`input-search-threads-expanded-${topic.id}`}
                                                    />
                                                    {threadSearchQuery && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setThreadSearchQuery("");
                                                            }}
                                                        >
                                                            <X className="h-3 w-3"/>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="max-h-64 overflow-y-auto">
                                                {availableThreadsToLink.length > 0 ? (
                                                    availableThreadsToLink.map((thread) => (
                                                        <DropdownMenuItem
                                                            key={thread.id}
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                setThreadSearchQuery("");
                                                                try {
                                                                    await linkThreadToTopic(thread, topic.id);
                                                                } catch (error) {
                                                                    console.error("Failed to link thread to topic:", error);
                                                                    toast({
                                                                        title: "Link Failed",
                                                                        description: error instanceof Error ? error.message : "Could not link thread to topic",
                                                                        variant: "destructive"
                                                                    });
                                                                }
                                                            }}
                                                            data-testid={`menu-link-thread-to-topic-expanded-${thread.id}`}
                                                        >
                                                            <MessageCircle className="h-3.5 w-3.5 mr-2"/>
                                                            <span className="truncate text-xs block max-w-[180px]">
                                                                {truncateTitle(thread.title || "Untitled Thread", TITLE_MAX_LENGTH)}
                                                            </span>
                                                        </DropdownMenuItem>
                                                    ))
                                                ) : (
                                                    <div
                                                        className="px-2 py-4 text-center text-xs text-muted-foreground">
                                                        {threadSearchQuery ? "No threads found" : "No threads available"}
                                                    </div>
                                                )}
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    )}
                                </div>
                            </div>
                            {allLinkedThreads.length > 0 ? (
                            <div className="space-y-1 pl-2 w-full min-w-0 max-w-full overflow-hidden">
                                {allLinkedThreads.map(thread => (
                                    <ThreadItem
                                        key={thread.id}
                                        thread={thread}
                                        viewMode="compact"
                                        spaceId={spaceId}
                                        topicId={topic.id}
                                        topicNameMap={{}}
                                        embeddingStatusMap={threadEmbeddingStatusMap}
                                        onRefresh={refreshKnowledge}
                                        compactTitleMaxLength={TITLE_MAX_LENGTH}
                                        spaceMemberRole={spaceMemberRole}
                                        spaceOwnerUserId={spaceOwnerUserId}
                                        itemOverrides={itemOverrides}
                                        isSharedSpace={isSharedSpace}
                                    />
                                ))}
                            </div>
                            ) : (
                                <p className="text-xs text-muted-foreground pl-2 py-1">No threads linked to this topic</p>
                            )}
                        </div>

                    {linkedDocuments.length > 0 && (
                        <div className="mb-3">
                            <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                    <FileText className="w-3 h-3"/>
                                    Documents ({linkedDocuments.length})
                                </div>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid={`button-new-doc-topic-${topic.id}`}>
                                                <Plus className="w-3 h-3 mr-1"/> New
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => setCreateDocDialogOpen(true)}>
                                                <FileText className="h-4 w-4 mr-2" /> New Document
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <FileUploadToNote spaceId={spaceId || null} folderId={null} topicId={topic.id} buttonLabel="Upload Files" multiple={true} onComplete={refetchKnowledgeForTopic} />
                                            </div>
                                            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <GoogleDrivePicker multiple={true} isFinalConfirmation={true} extractTextOnly={true} onFilesSelected={async (files: Array<{ text?: string; filename?: string }>) => {
                                                    const validFiles = files.filter((f) => f.text && typeof f.text === "string" && f.text.trim().length > 0);
                                                    if (validFiles.length === 0) { toast({ title: "No valid files", description: "No files with extractable text were found.", variant: "destructive" }); return; }
                                                    let successCount = 0; let failCount = 0;
                                                    for (let i = 0; i < validFiles.length; i++) {
                                                        const file = validFiles[i]; const filename = file.filename ?? `Untitled Document ${i + 1}`; const fileContent = "text" in file ? file.text ?? "" : "";
                                                        try {
                                                            await knowledgeBaseService.createKnowledgeBaseItem({ title: filename, content: fileContent, type: "note", spaceId: spaceId || null, topicIds: [topic.id], tagIds: [], folderId: null }, currentWorkspaceId ?? null);
                                                            successCount++;
                                                        } catch { failCount++; }
                                                    }
                                                    if (successCount > 0) refetchKnowledgeForTopic();
                                                    if (successCount > 0) toast({ title: "Notes created", description: `${successCount} note(s) created and linked to this topic${failCount > 0 ? `. ${failCount} failed.` : "."}` });
                                                    if (failCount > 0 && successCount === 0) toast({ title: "Creation failed", description: `Failed to create ${failCount} note(s).`, variant: "destructive" });
                                                }}>
                                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                                        <SiGoogledrive className="h-4 w-4 mr-2" /> Add from Google Drive
                                                    </DropdownMenuItem>
                                                </GoogleDrivePicker>
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <DocumentLinkerDrawer topicId={topic.id} spaceId={spaceId} trigger={<Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`button-link-documents-expanded-${topic.id}`}><Plus className="w-3 h-3 mr-1"/> Link</Button>} onLinksChanged={async () => { await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false }); refreshKnowledge?.(); }} />
                                </div>
                            </div>
                            <div className="space-y-1 pl-2 w-full min-w-0 max-w-full overflow-hidden">
                                {linkedDocuments.map(doc => (
                                    <DocumentItem
                                        key={doc.id}
                                        document={doc}
                                        viewMode="compact"
                                        spaceId={spaceId}
                                        topics={topics}
                                        topicNameMap={{}}
                                        onRefresh={refreshKnowledge}
                                        onDocumentClick={onDocumentClickProp}
                                        compactTitleMaxLength={TITLE_MAX_LENGTH}
                                        isSharedSpace={isSharedSpace}
                                        spaceMemberRole={spaceMemberRole}
                                        spaceOwnerUserId={spaceOwnerUserId}
                                        itemOverrides={itemOverrides}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {allLinkedThreads.length === 0 && childTopics.length === 0 && linkedDocuments.length === 0 && (
                        <div className="space-y-2">
                            <p className="text-xs text-muted-foreground py-2 text-center">
                                No threads, documents, or subtopics yet
                            </p>
                            <div className="flex justify-center gap-2">
                                {availableThreadsToLink.length > 0 && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`button-link-first-thread-to-topic-${topic.id}`}
                                            >
                                                <Plus className="w-3 h-3 mr-1"/>
                                                Link Thread
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="center" className="w-64 p-0">
                                            <div className="p-2 border-b">
                                                <div className="relative">
                                                    <Search
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"/>
                                                    <Input
                                                        placeholder="Search threads..."
                                                        value={threadSearchQuery}
                                                        onChange={(e) => setThreadSearchQuery(e.target.value)}
                                                        className="pl-7 h-8 text-xs"
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-testid={`input-search-threads-first-${topic.id}`}
                                                    />
                                                    {threadSearchQuery && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setThreadSearchQuery("");
                                                            }}
                                                        >
                                                            <X className="h-3 w-3"/>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="max-h-64 overflow-y-auto">
                                                {availableThreadsToLink.length > 0 ? (
                                                    availableThreadsToLink.map((thread) => (
                                                        <DropdownMenuItem
                                                            key={thread.id}
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                setThreadSearchQuery("");
                                                                try {
                                                                    await linkThreadToTopic(thread, topic.id);
                                                                } catch (error) {
                                                                    console.error("Failed to link thread to topic:", error);
                                                                    toast({
                                                                        title: "Link Failed",
                                                                        description: error instanceof Error ? error.message : "Could not link thread to topic",
                                                                        variant: "destructive"
                                                                    });
                                                                }
                                                            }}
                                                            data-testid={`menu-link-first-thread-to-topic-${thread.id}`}
                                                        >
                                                            <MessageCircle className="h-3.5 w-3.5 mr-2"/>
<span className="truncate text-xs block max-w-[180px]">
                                                                {truncateTitle(thread.title || "Untitled Thread", TITLE_MAX_LENGTH)}
                                                            </span>
                                                            </DropdownMenuItem>
                                                        ))
                                                    ) : (
                                                        <div
                                                            className="px-2 py-4 text-center text-xs text-muted-foreground">
                                                            {threadSearchQuery ? "No threads found" : "No threads available"}
                                                        </div>
                                                    )}
                                                </div>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`button-new-first-doc-topic-${topic.id}`}>
                                                <Plus className="w-3 h-3 mr-1"/> New
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="center">
                                            <DropdownMenuItem onClick={() => setCreateDocDialogOpen(true)}>
                                                <FileText className="h-4 w-4 mr-2" /> New Document
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <FileUploadToNote spaceId={spaceId || null} folderId={null} topicId={topic.id} buttonLabel="Upload Files" multiple={true} onComplete={refetchKnowledgeForTopic} />
                                            </div>
                                            <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <GoogleDrivePicker multiple={true} isFinalConfirmation={true} extractTextOnly={true} onFilesSelected={async (files: Array<{ text?: string; filename?: string }>) => {
                                                    const validFiles = files.filter((f) => f.text && typeof f.text === "string" && f.text.trim().length > 0);
                                                    if (validFiles.length === 0) { toast({ title: "No valid files", description: "No files with extractable text were found.", variant: "destructive" }); return; }
                                                    let successCount = 0; let failCount = 0;
                                                    for (let i = 0; i < validFiles.length; i++) {
                                                        const file = validFiles[i]; const filename = file.filename ?? `Untitled Document ${i + 1}`; const fileContent = "text" in file ? file.text ?? "" : "";
                                                        try {
                                                            await knowledgeBaseService.createKnowledgeBaseItem({ title: filename, content: fileContent, type: "note", spaceId: spaceId || null, topicIds: [topic.id], tagIds: [], folderId: null }, currentWorkspaceId ?? null);
                                                            successCount++;
                                                        } catch { failCount++; }
                                                    }
                                                    if (successCount > 0) refetchKnowledgeForTopic();
                                                    if (successCount > 0) toast({ title: "Notes created", description: `${successCount} note(s) created and linked to this topic${failCount > 0 ? `. ${failCount} failed.` : "."}` });
                                                    if (failCount > 0 && successCount === 0) toast({ title: "Creation failed", description: `Failed to create ${failCount} note(s).`, variant: "destructive" });
                                                }}>
                                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                                        <SiGoogledrive className="h-4 w-4 mr-2" /> Add from Google Drive
                                                    </DropdownMenuItem>
                                                </GoogleDrivePicker>
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <DocumentLinkerDrawer topicId={topic.id} spaceId={spaceId} trigger={<Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`button-link-first-document-${topic.id}`}><Plus className="w-3 h-3 mr-1"/> Link Document</Button>} onLinksChanged={async () => { await queryClient.refetchQueries({ queryKey: ["/api/knowledgebase"], exact: false }); refreshKnowledge?.(); }} />
                            </div>
                        </div>
                    )}
                </div>
            )}

            <TopicDocumentModal
                open={topicDocumentModalOpen}
                onOpenChange={setTopicDocumentModalOpen}
                spaceId={spaceId || null}
                topicId={topic.id}
                workspaceId={currentWorkspaceId ?? null}
                onFinished={refetchKnowledgeForTopic}
            />

            <Dialog open={createDocDialogOpen} onOpenChange={setCreateDocDialogOpen}>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>New Document</DialogTitle>
                        <DialogDescription>Create a document linked to this topic.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-doc-title">Title</Label>
                            <Input id="new-doc-title" placeholder="Untitled Document" value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} data-testid={`input-new-doc-title-${topic.id}`} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new-doc-content">Content (optional)</Label>
                            <Textarea id="new-doc-content" placeholder="Add content..." value={newDocContent} onChange={(e) => setNewDocContent(e.target.value)} rows={4} className="resize-none" data-testid={`input-new-doc-content-${topic.id}`} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDocDialogOpen(false)}>Cancel</Button>
                        <Button onClick={() => createDocumentMutation.mutate()} disabled={createDocumentMutation.isPending} data-testid={`button-save-new-doc-${topic.id}`}>
                            {createDocumentMutation.isPending ? "Creating..." : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </Card>

        {childTopics.length > 0 && (
            <div className="mt-1">
                <button
                    type="button"
                    onClick={() => setSubtopicsExpanded(v => !v)}
                    className="flex items-center gap-1 text-xs text-muted-foreground px-1 py-1 hover:text-foreground"
                    data-testid={`button-toggle-subtopics-${topic.id}`}
                >
                    {subtopicsExpanded ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
                    {childTopics.length} subtopic{childTopics.length !== 1 ? "s" : ""}
                </button>
                {subtopicsExpanded && (
                    <div className="pl-4 ml-3 border-l space-y-2">
                        {childTopics.map(child => renderTopicCard?.(child))}
                    </div>
                )}
            </div>
        )}
        </div>
    );
}
